"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

const Sheet = DialogPrimitive.Root;

const SheetTrigger = DialogPrimitive.Trigger;

const SheetPortal = DialogPrimitive.Portal;

const SheetClose = DialogPrimitive.Close;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 glass-overlay",
      "data-[state=open]:animate-in data-[state=closed]:animate-out",
      "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
));
SheetOverlay.displayName = "SheetOverlay";

type SheetSide = "left" | "right";

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  side?: SheetSide;
}

const SIDE_VARIANTS: Record<SheetSide, string> = {
  left: cn(
    "inset-y-0 left-0",
    "w-[min(22rem,calc(100vw-3.5rem))]",
    "data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left"
  ),
  right: cn(
    "inset-y-0 right-0",
    "w-[min(22rem,calc(100vw-3.5rem))]",
    "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right"
  ),
};

const SheetContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  SheetContentProps
>(({ side = "left", className, children, ...props }, ref) => (
  <SheetPortal>
    <SheetOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed z-50 h-dvh flex flex-col",
        "bg-background/95 backdrop-blur-xl",
        "border-white/[0.06]",
        side === "left" ? "border-r" : "border-l",
        "shadow-[4px_0_30px_rgba(0,0,0,0.5)]",
        "duration-250 ease-out",
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        SIDE_VARIANTS[side],
        className
      )}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </SheetPortal>
));
SheetContent.displayName = "SheetContent";

const SheetHeader = ({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex items-center justify-between px-4 h-12 shrink-0",
      "border-b border-white/[0.06]",
      className
    )}
    {...props}
  >
    {children}
    <SheetClose asChild>
      <button
        type="button"
        className={cn(
          "flex items-center justify-center h-11 w-11 rounded-lg",
          "text-foreground/40 hover:text-foreground/80 hover:bg-white/[0.06]",
          "transition-all duration-150",
          "focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-primary/50"
        )}
        aria-label="Close navigation"
      >
        <X className="size-4" />
      </button>
    </SheetClose>
  </div>
);
SheetHeader.displayName = "SheetHeader";

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-sm font-semibold text-foreground/90",
      className
    )}
    {...props}
  />
));
SheetTitle.displayName = "SheetTitle";

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetClose,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
};
